import React, { useState, useEffect } from "react";
import {
  Card,
  Divider,
  Tag,
  Button,
  Typography,
  Space,
  Modal,
  Descriptions,
  Spin,
  Badge,
  Row,
  Col,
  List,
} from "antd";
import "./RoomBookingList.css";
import Navbar from "../../../layouts/navbar";
import {
  EyeOutlined,
  CalendarOutlined,
  UserOutlined,
  HomeOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useAuth } from "../../../layouts/auth/AuthContext";
import { useRoom } from "./RoomContext";

const { Text, Title } = Typography;

const RoomBookingList = observer((props: any) => {
  const { user } = useAuth();
  const { fetchRoomBookings, roomBookings } = useRoom();
  const [isLatest, setIsLatest] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.UUID) {
      setLoading(true);
      fetchRoomBookings({ UUID: user?.UUID }).finally(() => setLoading(false));
    }
  }, [user?.UUID]);

  const showBookingDetails = (booking: any) => {
    setSelectedBooking(booking);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedBooking(null);
  };

  const getStatusConfig = (booking: any) => {
    switch (booking.booking_status) {
      case "PAYMENT SUCCESSFUL":
        return {
          color: "green",
          text: "Confirmed",
          icon: (
            <CheckCircleOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          ),
        };
      case "PENDING":
        return {
          color: "orange",
          text: "Pending",
          icon: (
            <ClockCircleOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          ),
        };
      case "CANCELLED":
        return {
          color: "red",
          text: "Cancelled",
          icon: (
            <CloseCircleOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          ),
        };
      default:
        return {
          color: "blue",
          text: booking.booking_status,
          icon: (
            <ClockCircleOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          ),
        };
    }
  };

  const getPaymentStatus = (booking: any) => {
    // Case 1: Fully paid and confirmed
    if (booking.booking_status === "PAYMENT SUCCESSFUL") {
      return { color: "green", text: "Fully Paid", type: "success" };
    }

    // Case 2: Has balance amount to pay
    if (
      booking.balance_need_to_pay &&
      parseFloat(booking.balance_need_to_pay) > 0
    ) {
      return {
        color: "orange",
        text: `Balance: RM ${parseFloat(booking.balance_need_to_pay).toFixed(
          2
        )}`,
        type: "balance",
      };
    }

    // Case 3: Unpaid status
    if (booking.balance_pay_status === "UNPAID") {
      return {
        color: "red",
        text: "Payment Required",
        type: "unpaid",
      };
    }

    // Case 4: Default based on booking status
    switch (booking.booking_status) {
      case "PENDING":
        return { color: "orange", text: "Pending Payment", type: "pending" };
      case "CANCELLED":
        return { color: "red", text: "Cancelled", type: "cancelled" };
      default:
        return { color: "blue", text: booking.booking_status, type: "unknown" };
    }
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format("DD MMM YYYY");
  };

  const formatDateTime = (dateString: string) => {
    return moment(dateString).format("DD MMM YYYY, HH:mm");
  };

  const calculateNights = (checkin: string, checkout: string) => {
    return moment(checkout).diff(moment(checkin), "days");
  };

  return (
    <div id="RoomBookingList">
      <div>
        <div className="page-header">
          <div>
            <div>
              <Text className="pageTitle">My Room Bookings</Text>
            </div>
            <div>
              <Text className="pageTitleExtra">
                Showing <b>{roomBookings?.length || 0}</b> bookings
              </Text>
            </div>
          </div>
          <div className="sort-control">
            <Text>Sort by: </Text>
            <Button
              type={isLatest ? "primary" : "default"}
              size="small"
              onClick={() => setIsLatest(true)}
            >
              Latest
            </Button>
            <Button
              type={!isLatest ? "primary" : "default"}
              size="small"
              onClick={() => setIsLatest(false)}
            >
              Oldest
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <Spin size="large" />
            <Text>Loading your bookings...</Text>
          </div>
        ) : roomBookings && roomBookings.length > 0 ? (
          <div className="bookings-container">
            {roomBookings
              .slice()
              .sort((a, b) =>
                isLatest
                  ? // @ts-ignore
                    new Date(b.booking_date) - new Date(a.booking_date)
                  : // @ts-ignore
                    new Date(a.booking_date) - new Date(b.booking_date)
              )
              .map((booking: any, index) => {
                const statusConfig = getStatusConfig(booking);
                const paymentConfig = getPaymentStatus(booking);
                const nights = calculateNights(
                  booking.checkin_date,
                  booking.checkout_date
                );

                return (
                  <Card key={booking.order_number} className="booking-card">
                    <Row gutter={[16, 16]} align="middle">
                      <Col xs={24} md={16}>
                        <div className="booking-main-info">
                          <div className="booking-header">
                            <div>
                              <Text
                                style={{ color: "darkgrey" }}
                              >{`#${booking.order_number}`}</Text>
                              <Title level={4} className="room-type">
                                {booking.room_type}
                              </Title>
                            </div>
                            <Badge
                              count={statusConfig.text}
                              style={{ backgroundColor: statusConfig.color }}
                              className="status-badge"
                            />
                          </div>

                          <div className="booking-dates">
                            <div className="dates-grid">
                              <div className="date-item">
                                <div className="date-text">
                                  <div className="date-label">Check-in</div>
                                  <div className="date-value">
                                    {formatDate(booking.checkin_date)}
                                  </div>
                                </div>
                              </div>

                              <div className="date-separator">→</div>

                              <div className="date-item">
                                <div className="date-text">
                                  <div className="date-label">Check-out</div>
                                  <div className="date-value">
                                    {formatDate(booking.checkout_date)}
                                  </div>
                                </div>
                              </div>

                              <div className="nights-badge">
                                <div className="nights-count">{nights}</div>
                                <div className="nights-label">
                                  night{nights > 1 ? "s" : ""}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="booking-details">
                            <Space size="large" wrap>
                              <div className="detail-item">
                                <UserOutlined
                                  className="info-icon"
                                  onPointerEnterCapture={undefined}
                                  onPointerLeaveCapture={undefined}
                                />
                                <Text>{booking.adult_pax} Adults</Text>
                                {booking.children_pax > 0 && (
                                  <Text>, {booking.children_pax} Children</Text>
                                )}
                              </div>
                              <div className="detail-item">
                                <HomeOutlined
                                  className="info-icon"
                                  onPointerEnterCapture={undefined}
                                  onPointerLeaveCapture={undefined}
                                />
                                <Text>
                                  {booking.room_quantity} Room
                                  {booking.room_quantity > 1 ? "s" : ""}
                                </Text>
                              </div>
                              <div className="detail-item">
                                <DollarOutlined
                                  className="info-icon"
                                  onPointerEnterCapture={undefined}
                                  onPointerLeaveCapture={undefined}
                                />
                                <Tag color={paymentConfig.color}>
                                  {paymentConfig.text}
                                </Tag>
                              </div>
                            </Space>
                          </div>

                          {booking.Addon && booking.Addon.length > 0 && (
                            <div className="addons-section">
                              <Text strong>Add-ons: </Text>
                              {booking.Addon.map((addon: any, idx: number) => (
                                <Tag
                                  key={idx}
                                  color="blue"
                                  className="addon-tag"
                                >
                                  {addon.addon_detail} (x{addon.addon_pax})
                                </Tag>
                              ))}
                            </div>
                          )}
                        </div>
                      </Col>

                      <Col xs={24} md={8}>
                        <div className="booking-side-info">
                          <div className="price-section">
                            <Title level={3} className="total-price">
                              RM {parseFloat(booking.total_pay).toFixed(2)}
                            </Title>
                            <Text type="secondary" className="payment-type">
                              {booking.pay_type}
                            </Text>
                          </div>

                          <div className="booking-meta">
                            {/* <Text type="secondary" className="order-number">
                              Order: {booking.order_number}
                            </Text> */}
                            <Text type="secondary" className="booking-date">
                              Booked on {formatDateTime(booking.booking_date)}
                            </Text>
                          </div>

                          <Button
                            type="primary"
                            icon={
                              <EyeOutlined
                                onPointerEnterCapture={undefined}
                                onPointerLeaveCapture={undefined}
                              />
                            }
                            onClick={() => showBookingDetails(booking)}
                            className="view-details-btn"
                          >
                            View Details
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                );
              })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🏨</div>
            <Title level={3}>No Bookings Found</Title>
            <Text type="secondary">
              You haven't made any room bookings yet.
            </Text>
          </div>
        )}

        {/* Booking Details Modal 
        @ts-ignore */}
        <Modal
          title="Booking Details"
          visible={isModalVisible}
          onCancel={handleCloseModal}
          footer={[
            <Button key="close" onClick={handleCloseModal}>
              Close
            </Button>,
          ]}
          width={700}
          className="booking-details-modal"
        >
          {selectedBooking && (
            <div className="modal-content">
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Order Number">
                  <Text strong>{selectedBooking.order_number}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Room Type">
                  <Text strong>{selectedBooking.room_type}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Status">
                  <Tag color={getStatusConfig(selectedBooking).color}>
                    {getStatusConfig(selectedBooking).text}
                  </Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Stay Duration">
                  {formatDate(selectedBooking.checkin_date)} to{" "}
                  {formatDate(selectedBooking.checkout_date)}
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    (
                    {calculateNights(
                      selectedBooking.checkin_date,
                      selectedBooking.checkout_date
                    )}{" "}
                    nights)
                  </Text>
                </Descriptions.Item>

                <Descriptions.Item label="Guests">
                  {selectedBooking.adult_pax} Adults
                  {selectedBooking.children_pax > 0 &&
                    `, ${selectedBooking.children_pax} Children`}
                </Descriptions.Item>

                <Descriptions.Item label="Rooms">
                  {selectedBooking.room_quantity} Room
                  {selectedBooking.room_quantity > 1 ? "s" : ""}
                </Descriptions.Item>

                <Descriptions.Item label="Contact Information">
                  <div>
                    <Text strong>{selectedBooking.contact_name}</Text>
                    <br />
                    <Text type="secondary">
                      {selectedBooking.contact_phone}
                    </Text>
                    <br />
                    <Text type="secondary">
                      {selectedBooking.contact_email}
                    </Text>
                  </div>
                </Descriptions.Item>

                <Descriptions.Item label="Payment Information">
                  <div>
                    <Text strong>
                      Total Amount: RM{" "}
                      {parseFloat(selectedBooking.total_pay).toFixed(2)}
                    </Text>
                    <br />
                    <Text type="secondary">
                      Original Price: RM{" "}
                      {parseFloat(selectedBooking.origin_total).toFixed(2)}
                    </Text>
                    <br />
                    <Text type="secondary">
                      Payment Type: {selectedBooking.pay_type}
                    </Text>
                    {/* <br /> */}
                    {/* <Text type="secondary">
                      Status: {getPaymentStatus(selectedBooking).text}
                    </Text> */}
                    {selectedBooking.balance_need_to_pay &&
                      parseFloat(selectedBooking.balance_need_to_pay) > 0 && (
                        <>
                          <br />
                          <Text type="warning">
                            Balance Due: RM{" "}
                            {parseFloat(
                              selectedBooking.balance_need_to_pay
                            ).toFixed(2)}
                          </Text>
                        </>
                      )}
                    {selectedBooking.balance_pay_expired_date && (
                      <>
                        <br />
                        <Text type="warning">
                          Pay before:{" "}
                          {formatDate(selectedBooking.balance_pay_expired_date)}
                        </Text>
                      </>
                    )}
                    {selectedBooking.discount_detail &&
                      selectedBooking.discount_detail !== "DISCOUNT 0.00" && (
                        <>
                          <br />
                          <Text type="success">
                            Discount: {selectedBooking.discount_detail}
                          </Text>
                        </>
                      )}
                  </div>
                </Descriptions.Item>

                {selectedBooking.Addon && selectedBooking.Addon.length > 0 && (
                  <Descriptions.Item label="Add-ons">
                    {selectedBooking.Addon.map((addon: any, idx: number) => (
                      <div key={idx} style={{ marginBottom: 8 }}>
                        <Text strong>{addon.addon_detail}</Text>
                        <br />
                        <Text type="secondary">
                          Pax: {addon.addon_pax} × Nights: {addon.addon_nights}{" "}
                          = RM {addon.addon_total}
                        </Text>
                      </div>
                    ))}
                  </Descriptions.Item>
                )}

                {selectedBooking.remark &&
                  selectedBooking.remark !== "none" && (
                    <Descriptions.Item label="Special Requests">
                      {selectedBooking.remark}
                    </Descriptions.Item>
                  )}

                <Descriptions.Item label="Booking Date">
                  {formatDateTime(selectedBooking.booking_date)}
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
});

export default RoomBookingList;
